import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { StepConfigItem } from '~/Interfaces';
import {
  companyIsSoleTrader,
  companyIsStockCompany,
} from '~/constants/markets';
import { SignerType } from '~/enums';
import { isBeneficialOwner, personsAreTheSame } from '~/helpers/shared.helper';
import { stepConfigPerMarket } from '~/pages/CardOffer/components/stepConfigs';
import { CardMarket } from '~/types/types';
import { RootState } from '..';

const selectApplicantHasAnsweredBeneficialOwnersQuestion = createSelector(
  [(state: RootState) => state.cardOffer.form.companyHasOtherBeneficialOwners],
  (companyHasOtherBeneficialOwners) =>
    typeof companyHasOtherBeneficialOwners === 'boolean'
);

const selectApplicantHasAnswered100PercentOwnershipQuestion = createSelector(
  [(state: RootState) => state.cardOffer.form.applicantOwns100PercentOfShares],
  (applicantOwns100PercentOfShares) =>
    typeof applicantOwns100PercentOfShares === 'boolean'
);

const selectCompanyIsStockCompany = createSelector(
  [
    (state: RootState) => state.cardOffer.overview.companyType,
    (state: RootState) => state.intl.market,
  ],
  (companyType, market) => companyIsStockCompany(market, companyType)
);

const selectApplicantIsABeneficialOwner = createSelector(
  [(state: RootState) => state.cardOffer.form.applicant.percentage],
  (applicantPercentage) => isBeneficialOwner(applicantPercentage)
);

const selectApplicantIsSoloBeneficialOwner = createSelector(
  [
    (state: RootState) => state.cardOffer.form.applicant.percentage,
    (state: RootState) => state.cardOffer.form.companyHasOtherBeneficialOwners,
  ],
  (applicantPercentage, companyHasOtherBeneficialOwners) =>
    isBeneficialOwner(applicantPercentage) &&
    (companyHasOtherBeneficialOwners === false ||
      Number(applicantPercentage) >= 75)
);

const selectOwnersDataIsFetched = createSelector(
  [(state: RootState) => state.cardOffer.overview.beneficialOwners],
  (beneficialOwners) => !!beneficialOwners.length
);
const selectTotalAmountOfShares = createSelector(
  [
    (state: RootState) => state.cardOffer.form.applicant.percentage,
    (state: RootState) => state.cardOffer.form.owners,
    selectOwnersDataIsFetched,
  ],
  (applicantPercentage, owners, ownersDataIsFetched) => {
    if (ownersDataIsFetched) {
      return owners
        .filter((owner) => !owner.isAlternativeBeneficialOwner)
        .reduce((acc, owner) => acc + Number(owner.percentage), 0);
    }
    return owners
      .filter((owner) => !owner.isAlternativeBeneficialOwner)
      .reduce(
        (acc, owner) => acc + Number(owner.percentage),
        Number(applicantPercentage)
      );
  }
);
const selectFetchedOwnersHaveBeenChanged = createSelector(
  [
    (state: RootState) => state.cardOffer.form.owners,
    (state: RootState) => state.cardOffer.overview.beneficialOwners,
  ],
  (owners, beneficialOwners) => {
    if (!beneficialOwners.length) {
      return false;
    }

    const hasChanged =
      beneficialOwners.length !== owners.length ||
      beneficialOwners.some((fetchedOwner) => {
        const owner = owners.find((o) => o.id === fetchedOwner.id);

        if (
          !owner ||
          owner.fullName !== fetchedOwner.fullName ||
          owner.personalNumber !== fetchedOwner.personalNumber ||
          owner.percentage !== fetchedOwner.percentage ||
          owner.countryOfResidence !== fetchedOwner.countryOfResidence ||
          (fetchedOwner.dateOfBirth &&
            moment(owner.dateOfBirth).format('YYYY-MM-DD') !==
              moment(fetchedOwner.dateOfBirth).format('YYYY-MM-DD')) ||
          owner.yearOfBirth !== fetchedOwner.yearOfBirth
        ) {
          return true;
        }

        return false;
      });

    return hasChanged;
  }
);

const selectAlternativeOwnerManuallyAdded = createSelector(
  [(state: RootState) => state.cardOffer.form.owners],
  (owners) => {
    return owners.some((owner) => owner.isAlternativeBeneficialOwner);
  }
);

export const selectStepConfig = createSelector(
  [
    (state: RootState) => state.cardOffer.form.owners,
    (state: RootState) => state.cardOffer.form.applicant,
    (state: RootState) => state.cardOffer.overview.companyType,
    (state: RootState) => state.cardOffer.overview.additionalSigners,
    (state: RootState) => state.cardOffer.overview.applicationReasons,
    (state: RootState) => state.intl.market,
  ],
  (owners, applicant, companyType, signers, cardPurpose, market) => {
    const stepConfig = stepConfigPerMarket[market as CardMarket].filter(
      (step: StepConfigItem) => {
        let hideStep = false;

        switch (step.name) {
          case 'card_purpose_step': // hide card purpose step if we already have card purposes (application reasons)
            hideStep = !!cardPurpose?.length;
            break;
          case 'ownership_step': // hide ownership step if company is sole trader
            hideStep = companyIsSoleTrader(market, companyType);
            break;
          case 'additional_signers_and_guarantors_step': {
            // hide step if there are no additional signers and the applicant is an owner.
            // If the applicant is an owner, it means that the AML/KYC data will be added to the applicant in the owners step.
            const noAdditionalSigners = !signers.filter(
              (signer) => signer.type === SignerType.Signer
            ).length;
            const applicantIsAnOwner = !!owners.find((owner) =>
              personsAreTheSame(owner, applicant)
            );

            hideStep = noAdditionalSigners && applicantIsAnOwner;
            break;
          }
          default:
            break;
        }

        return !hideStep;
      }
    );
    return stepConfig;
  }
);

export const useCardOfferSelectors = () => ({
  applicantHasAnsweredBeneficialOwnersQuestion: useSelector(
    selectApplicantHasAnsweredBeneficialOwnersQuestion
  ),
  applicantHasAnswered100PercentOwnershipQuestion: useSelector(
    selectApplicantHasAnswered100PercentOwnershipQuestion
  ),
  companyIsStockCompany: useSelector(selectCompanyIsStockCompany),
  applicantIsABeneficialOwner: useSelector(selectApplicantIsABeneficialOwner),
  applicantIsSoloBeneficialOwner: useSelector(
    selectApplicantIsSoloBeneficialOwner
  ),
  totalAmountOfShares: useSelector(selectTotalAmountOfShares),
  ownersDataIsFetched: useSelector(selectOwnersDataIsFetched),
  fetchedOwnersHaveBeenChanged: useSelector(selectFetchedOwnersHaveBeenChanged),
  alternativeOwnerManuallyAdded: useSelector(
    selectAlternativeOwnerManuallyAdded
  ),
  stepConfig: useSelector(selectStepConfig),
});
